import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: 'website' | 'article' | 'business.business';
  ogImage?: string;
  noIndex?: boolean;
  structuredData?: object;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const BASE_URL = 'https://wellnessconnect.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-wellness-connect.png`;
const DEFAULT_KEYWORDS = 'wellness, health and wellness services, holistic health, mental wellness, book wellness services online, wellness clinics near me, wellness practitioners, health coaches, online wellness booking, spa, massage therapy, yoga meditation, fitness nutrition';

// Organization structured data
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Wellness Connect",
  "url": BASE_URL,
  "logo": `${BASE_URL}/logo.png`,
  "description": "The global online wellness booking platform.",
  "sameAs": [
    "https://facebook.com/wellnessconnect",
    "https://instagram.com/wellnessconnect",
    "https://twitter.com/WellnessConnect"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "hello@wellnessconnect.com",
    "contactType": "customer service",
    "availableLanguage": ["English"]
  }
};

// Website structured data with search action
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Wellness Connect",
  "url": BASE_URL,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${BASE_URL}/browse?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

export function SEOHead({
  title,
  description = 'Discover top-rated wellness practitioners, spas, massage therapists, and holistic health centers worldwide. Book health and wellness services online.',
  keywords = DEFAULT_KEYWORDS,
  canonicalUrl,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  structuredData,
  author,
  publishedTime,
  modifiedTime,
}: SEOHeadProps) {
  const fullTitle = title 
    ? `${title.slice(0, 50)} | Wellness Connect`.slice(0, 60)
    : 'Wellness Connect - Book Wellness Services Online';
  
  const truncatedDescription = description.slice(0, 155);
  
  const fullCanonicalUrl = canonicalUrl ? `${BASE_URL}${canonicalUrl}` : BASE_URL;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={truncatedDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author || 'Wellness Connect'} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en" />
      <meta property="og:site_name" content="Wellness Connect" />

      {/* Article specific meta */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@WellnessConnect" />

      {/* Language */}
      <meta httpEquiv="content-language" content="en" />
      <link rel="alternate" hrefLang="en" href={fullCanonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullCanonicalUrl} />

      {/* Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Website Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {/* Page-specific Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
